import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCancelReasons } from '@/features/cancellationQuote/api';
import type { CancelReason } from '@/features/cancellationQuote/types';
import { useHybridForm } from '@/hooks/useHybridForm';
import { useForm, useStore } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// ----- Form-specific types and schemas -----

export interface CancellationDetailsFormSubmitValues {
  cancelDate: Date;
  vehicleMileage: number;
  cancelReasonId: string;
}

// Define field-level schemas first
const cancelDateSchema = z
  .date()
  .max(new Date(new Date().setHours(23, 59, 59, 999)), {
    message: 'Cancel date cannot be in the future.',
  });

const createVehicleMileageSchema = (
  requiresMileage: boolean,
  isOnSubmit: boolean
) => {
  if (requiresMileage) {
    if (isOnSubmit) {
      // Full validation for submit: required + type + positive
      return z.coerce
        .number({
          required_error:
            'Vehicle mileage is required for Service and Maintenance contracts.',
          invalid_type_error: 'Vehicle mileage must be a number.',
        })
        .positive({
          message:
            'Vehicle mileage must be greater than 0 for Service and Maintenance contracts.',
        });
    } else {
      // During onChange: allow empty values (0, undefined, NaN) or positive numbers
      return z.coerce
        .number()
        .optional()
        .refine(
          (val) => {
            // Allow undefined, 0, NaN (empty input states)
            if (val === undefined || val === 0 || isNaN(val)) {
              return true;
            }
            // If a value is provided, it must be positive
            return val > 0;
          },
          {
            message:
              'Vehicle mileage must be greater than 0 for Service and Maintenance contracts.',
          }
        );
    }
  }
  return z.coerce.number().optional().default(0);
};

const createCancelReasonIdSchema = (isOnSubmit: boolean) => {
  if (isOnSubmit) {
    // Full validation for submit: required + min length
    return z
      .string({
        required_error: 'Cancel reason is required.',
      })
      .min(1, 'Cancel reason is required.');
  }
  // No validation on change, but must be string type
  return z.string();
};

// Define the FormValues type from the schemas
interface FormValues {
  cancelDate: z.infer<typeof cancelDateSchema>;
  vehicleMileage?: number;
  cancelReasonId: string;
}

// Define default values matching FormValues
const FORM_DEFAULT_VALUES: FormValues = {
  cancelDate: new Date(),
  vehicleMileage: 0, // Default to 0, validation will handle if it needs to be > 0
  cancelReasonId: '',
};

// Define object that will be used to create the Zod schema for the entire
// form using the field schemas
const createFormValidationSchema = (
  requiresMileage: boolean,
  isOnSubmit: boolean
) =>
  z.object({
    cancelDate: cancelDateSchema, // Always validate cancel date
    vehicleMileage: createVehicleMileageSchema(requiresMileage, isOnSubmit),
    cancelReasonId: createCancelReasonIdSchema(isOnSubmit),
  });

// ----- Component props -----

interface CancellationDetailsFormProps {
  onSubmit: (values: CancellationDetailsFormSubmitValues) => void;
  isSubmitting: boolean;
  requiresVehicleMileage: boolean;
}

// ----- Component implementation -----

export function CancellationDetailsForm({
  onSubmit,
  isSubmitting,
  requiresVehicleMileage,
}: CancellationDetailsFormProps) {
  const {
    data: cancelReasons,
    isLoading: isLoadingCancelReasons,
    error: cancelReasonsQueryError,
    isError: isCancelReasonsError,
  } = useQuery<CancelReason[], Error>({
    queryKey: ['cancelReasons'],
    queryFn: () => getCancelReasons(),
  });

  React.useEffect(() => {
    if (isCancelReasonsError && cancelReasonsQueryError) {
      toast.error(cancelReasonsQueryError.message);
    }
  }, [isCancelReasonsError, cancelReasonsQueryError]);

  const form = useForm({
    defaultValues: FORM_DEFAULT_VALUES,
    onSubmit: async ({ value }) => {
      // Ensure vehicleMileage is a number
      const submitValues = {
        ...value,
        vehicleMileage: value.vehicleMileage ?? 0,
      };
      onSubmit(submitValues as CancellationDetailsFormSubmitValues);
    },
    validators: {
      onChange: createFormValidationSchema(requiresVehicleMileage, false),
      onSubmit: createFormValidationSchema(requiresVehicleMileage, true),
    },
  });

  const rhfForm = useHybridForm(
    form,
    Object.keys(FORM_DEFAULT_VALUES) as Array<keyof FormValues>,
    FORM_DEFAULT_VALUES as unknown as Record<string, unknown>
  );

  // Subscribe to specific form state properties to ensure proper reactivity
  // This is the recommended approach from TanStack Form documentation to avoid
  // unnecessary re-renders while ensuring the button state updates correctly
  const { isValid, isSubmitting: formIsSubmitting } = useStore(
    form.store,
    (state) => ({
      isValid: state.isValid,
      isSubmitting: state.isSubmitting,
    })
  );

  return (
    <FormProvider {...rhfForm}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className='space-y-6'
      >
        <FormField
          name='cancelDate'
          render={() => (
            <form.Field name='cancelDate'>
              {(field) => (
                <FormItem className='flex flex-col'>
                  <FormLabel>Cancel Date</FormLabel>
                  <DatePicker
                    value={field.state.value}
                    onChange={(date) => field.handleChange(date ?? new Date())}
                    disabled={(dateToEvaluate: Date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0); // Set to midnight to compare dates accurately

                      const tomorrow = new Date(today);
                      tomorrow.setDate(today.getDate() + 1); // This is the start of tomorrow

                      const ninetyDaysPrior = new Date(today);
                      ninetyDaysPrior.setDate(today.getDate() - 90); // This is the start of the day 90 days ago

                      // Disable if the dateToEvaluate is on or after tomorrow,
                      // or if it's on or before ninetyDaysPrior.
                      return (
                        dateToEvaluate >= tomorrow ||
                        dateToEvaluate <= ninetyDaysPrior
                      );
                    }}
                    placeholder='Select cancel date'
                  />
                  <FormMessage />
                  <p className='text-sm text-warning font-medium mt-1'>
                    Contact TCA for any backdated quotes beyond the allowable
                    date range.
                  </p>
                </FormItem>
              )}
            </form.Field>
          )}
        />

        <FormField
          name='vehicleMileage'
          render={() => (
            <form.Field name='vehicleMileage'>
              {(field) => (
                <FormItem>
                  <FormLabel>Vehicle Mileage</FormLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type='number'
                    placeholder={
                      requiresVehicleMileage
                        ? 'Enter vehicle mileage'
                        : 'Vehicle mileage (Not required)'
                    }
                    value={
                      field.state.value === 0 ||
                      field.state.value === undefined ||
                      isNaN(field.state.value ?? 0)
                        ? ''
                        : field.state.value.toString()
                    }
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(Number(e.target.value) || 0)
                    }
                    onKeyDown={(e) => {
                      // Prevent arrow keys from changing the value
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                      }
                    }}
                    disabled={!requiresVehicleMileage}
                  />
                  <FormMessage />
                  {!requiresVehicleMileage && (
                    <p className='text-sm text-muted-foreground'>
                      Vehicle mileage is not required for the selected
                      contracts.
                    </p>
                  )}
                </FormItem>
              )}
            </form.Field>
          )}
        />

        <FormField
          name='cancelReasonId'
          render={() => (
            <form.Field name='cancelReasonId'>
              {(field) => (
                <FormItem>
                  <FormLabel>Cancel Reason</FormLabel>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={
                      isLoadingCancelReasons || !!cancelReasonsQueryError
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a reason' />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingCancelReasons && (
                        <SelectItem value='loading' disabled>
                          Loading reasons...
                        </SelectItem>
                      )}
                      {isCancelReasonsError && (
                        <SelectItem value='error' disabled>
                          Could not load reasons
                        </SelectItem>
                      )}
                      {cancelReasons?.map((reason) => (
                        <SelectItem key={reason.id} value={String(reason.id)}>
                          {reason.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            </form.Field>
          )}
        />

        <Button
          type='submit'
          disabled={isSubmitting || !isValid || formIsSubmitting}
        >
          {isSubmitting || formIsSubmitting
            ? 'Getting Quotes...'
            : 'Get Quotes'}
        </Button>
      </form>
    </FormProvider>
  );
}
